import { useState, useEffect, useCallback, useRef } from 'react';
import { api, GrGameConfigDetail } from '@/lib/api';

interface UseGameConfigsReturn {
  gameConfigs: GrGameConfigDetail[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useGameConfigs(): UseGameConfigsReturn {
  const [gameConfigs, setGameConfigs] = useState<GrGameConfigDetail[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const fetchGameConfigs = useCallback(async () => {
    const id = ++fetchIdRef.current;
    try {
      setLoading(true);
      const result = await api.listGameRewardsGames();
      if (fetchIdRef.current !== id) return;
      setGameConfigs(result.gameConfigs);
      setTotal(result.total);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load game configs');
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, []);

  const refresh = useCallback(() => {
    setError(null);
    fetchGameConfigs();
  }, [fetchGameConfigs]);

  useEffect(() => {
    refresh();
    return () => {
      fetchIdRef.current++;
    };
  }, [refresh]);

  return { gameConfigs, total, loading, error, refresh };
}
